import { useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { request } from 'utils/api'
import { CHATGPT_IMAGE_VALIDATION_ENABLED } from 'constants/imageValidation'
import { getUploadErrorMessage, MAX_SOURCE_IMAGE_BYTES, SOURCE_IMAGE_SIZE_MESSAGE, IMAGE_SIZE_MESSAGE, MAX_IMAGE_BYTES, parseImageUploadResponse } from './uploadErrors'
import { isProd } from 'utils/functions'

const MAX_FILE_SIZE = MAX_SOURCE_IMAGE_BYTES
const SUPPORTED_IMAGE_FORMATS = ['image/jpg', 'image/jpeg', 'image/png']
const VALIDATION_URL = 'https://www.hiddenunderground.com/imagevalidation/analyze-imagee/'

const useImageUpload = ({ uploadUrl, onSuccess }) => {
  const inputRef = useRef(null)
  const onChangeRef = useRef(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState(null)
  const [showHelperText, setShowHelperText] = useState(true)
  const [cropDialogOpen, setCropDialogOpen] = useState(false)
  const [imageToCrop, setImageToCrop] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const { enqueueSnackbar } = useSnackbar()

  const rejectFile = (message) => {
    enqueueSnackbar(message, { variant: 'error' })
    setUploadStatus({ message: 'Not accepted', color: 'red' })
  }

  const rejectFileSize = () => rejectFile(SOURCE_IMAGE_SIZE_MESSAGE)
  const rejectFileType = () => rejectFile('Please choose only .jpg or .png images')

  const processFile = (file, onChange) => {
    if (!file) return
    setShowHelperText(false)

    if (file.size > MAX_FILE_SIZE) {
      rejectFileSize()
      return
    }

    const fileName = file.name.toLowerCase()
    const supportedType = file.type && SUPPORTED_IMAGE_FORMATS.includes(file.type)
    const supportedExtension = ['.jpg', '.jpeg', '.png']
      .some((extension) => fileName.endsWith(extension))
    if (!supportedType && !supportedExtension) {
      rejectFileType()
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      if (reader.result) {
        onChangeRef.current = onChange
        setImageToCrop(reader.result)
        setCropDialogOpen(true)
      }
    }
    reader.readAsDataURL(file)
  }

  const closeCropDialog = () => {
    setCropDialogOpen(false)
    setImageToCrop(null)
    onChangeRef.current = null
  }

  const uploadImage = (image, onChange) => {
    const formData = new FormData()
    formData.append('file', image)
    setIsUploading(true)

    request(uploadUrl, {
      method: 'POST',
      body: formData,
      mode: 'cors',
      withCredentials: true,
      credentials: 'include',
    }, parseImageUploadResponse)
      .then((response) => {
        const host = isProd() ? window.location.origin : 'https://staging.hiddenunderground.com'
        const url = `${host}${response.result}`
        const reader = new FileReader()
        reader.onload = () => setPreviewUrl(reader.result)
        reader.readAsDataURL(image)

        onChange?.(url)
        onSuccess?.(url, response)
        enqueueSnackbar('Image uploaded', { variant: 'success' })
        setUploadStatus({ message: 'Accepted', color: 'green' })
      })
      .catch((error) => {
        if (error.sessionHandled) return
        console.error('Upload failed:', error)
        enqueueSnackbar(getUploadErrorMessage(error), { variant: 'error' })
        setUploadStatus({ message: 'Not accepted', color: 'red' })
      })
      .finally(() => {
        setIsUploading(false)
      })
  }

  const validateImage = (image, onChange) => {
    if (image.size > MAX_IMAGE_BYTES) {
      rejectFile(IMAGE_SIZE_MESSAGE)
      return
    }
    if (!CHATGPT_IMAGE_VALIDATION_ENABLED) {
      uploadImage(image, onChange)
      return
    }

    const formData = new FormData()
    formData.append('file', image)
    setIsUploading(true)
    fetch(VALIDATION_URL, { method: 'POST', body: formData })
      .then((response) => response.json())
      .then((response) => {
        if (response?.answer) {
          if (response.answer.trim().toLowerCase().startsWith('no')) {
            uploadImage(image, onChange)
            return
          }
          rejectFile('Inappropriate image, cannot upload it')
          setIsUploading(false)
          return
        }
        rejectFile("We couldn't validate your image. Please try again in a few minutes.")
        setIsUploading(false)
      })
      .catch((error) => {
        console.error('Validation failed:', error)
        rejectFile("We couldn't validate your image. Please try again in a few minutes.")
        setIsUploading(false)
      })
  }

  const handleCropComplete = (croppedFile) => {
    const onChange = onChangeRef.current
    onChangeRef.current = null
    setCropDialogOpen(false)
    setImageToCrop(null)
    if (croppedFile) validateImage(croppedFile, onChange)
  }

  const handleImageSelect = (event, onChange) => {
    const [file] = event.target.files
    processFile(file, onChange)
    event.target.value = ''
  }

  return {
    closeCropDialog,
    cropDialogOpen,
    handleCropComplete,
    handleImageSelect,
    imageToCrop,
    inputRef,
    isUploading,
    openFileDialog: () => inputRef.current?.click(),
    previewUrl,
    processFile,
    rejectFileSize,
    rejectFileType,
    showHelperText,
    uploadStatus,
  }
}

export default useImageUpload
